import React, { useState } from 'react'
import translate from '../../../core/translate'
import Content from 'blocks.app/content/content'
import Catalog from '../../../blocks.app/catalog/catalog'
import Sidebar from '../../../blocks.app/sidebar/sidebar'
import styles from './broadcastEvents.jcss'
import { InputField } from '../../../blocks.simple/InputField/InputField'
import Dropdown from '../../../blocks.simple/dropdown/dropdown'
import Button from '../../../blocks.simple/button/button'
import { api } from '../../../core/api/ConnectionManager'
import { cn } from 'ethcss'
import { colors, grid } from '../../../theme'
import ReactJson from 'react-json-view'
import { RootStateOrAny, useSelector } from 'react-redux'
import { changeLocation, routes } from '../../../features/routes'
import Icon from '../../../blocks.simple/icon/icon'
import Toast from '../../../blocks.simple/toast/toast'
import { SidebarMultiselectMenu } from '../../../blocks.app/sidebarMultiselectMenu/SidebarMultiselectMenu'
import { MenuItem } from '../../displays/__toolbarContent/displays__toolbarContent'

export interface IEvent {
    priority: number
    id?: number | undefined
    name: string
    eventType: string | null
    key: string | null
    sample?: any
    condition?: {
        value: string | null
        comparisonType: string
    }
    conditionList?: {
        value: string | null
        comparisonType: string
        pathToField: string
    }[]
}

type EventData = {
    id?: number | undefined
    name: string
    eventType: string | null
    priority: string
    key: string | null
    value: string | null
    json?: string
    pathToField?: string
}

export const BroadcastEvents = () => {
    const defaultEventData = {
        name: '',
        eventType: null,
        priority: 'low',
        key: null,
        value: null,
        json: null,
    }
    const company: any = useSelector<RootStateOrAny>((state) => state.user.company)
    const [sampleInfo, setSampleInfo] = useState<any>(null)
    const [activeTab, setActiveTab] = useState<string>('broadcastEvents')
    const [selectedInfo, setSelectedInfo] = useState<IEvent | null>(null)
    const [selectedItems, setSelectedItems] = useState<IEvent[]>([])
    const [eventModalData, setEventModalData] = useState<any>(null)
    const [deleteConfirm, setDeleteConfirm] = useState<(number | undefined)[]>([])
    const [eventInfo, setEventInfo] = useState<IEvent | null>(null)

    const priorityOptions = [
        { id: 'low', name: translate('lowPriority') },
        { id: 'medium', name: translate('mediumPriority') },
        { id: 'high', name: translate('highPriority') },
    ]

    const eventsOptions = [
        { id: 'face_recognition', name: translate('face_recognition'), key: ['gender'] },
        { id: 'rfid', name: translate('rfidTag'), key: ['tagId', 'label'] },
        { id: 'server_event', name: translate('server_event'), key: ['label'] },
        { id: 'digital_signage_event', name: translate('digital_signage_event'), key: ['active_player'] },
    ]

    const getKeyOptions = () => {
        switch (eventModalData && eventModalData.eventType) {
            case 'face_recognition':
                return [{ id: 'gender', name: translate('face_recognition_gender') }]
            case 'rfid':
                return [
                    { id: 'tagId', name: translate('rfid_tagId') },
                    { id: 'label', name: translate('rfid_label') },
                ]
            case 'server_event':
                return [
                    { id: 'label', name: translate('server_event_label') },
                    { id: 'external_source', name: translate('server_event_external_source') },
                ]
            case 'digital_signage_event':
                return [{ id: 'active_player', name: translate('digital_signage_event_active_player') }]
            default:
                return []
        }
    }

    const getValueOptions = () => {
        switch (eventModalData && eventModalData.key) {
            case 'gender':
                return [
                    { id: 'male', name: translate('male') },
                    { id: 'female', name: translate('female') },
                ]
            case 'active_player':
                return [{ id: 'playbackError', name: translate('playbackError') }]
            default:
                return []
        }
    }

    const renderValueField = (data: IEvent) => {
        if (!data || !data.key) return null

        if (data.key && (data.eventType === 'face_recognition' || data.eventType === 'digital_signage_event')) {
            return (
                <Dropdown
                    value={getValue('value')}
                    options={getValueOptions()}
                    label={translate('value')}
                    onChange={(value) => {
                        onChangeField('value', value.id)
                    }}
                />
            )
        }

        return (
            data.key && (
                <InputField
                    label={translate('value')}
                    value={getValue('value')}
                    onChange={(value: string) => onChangeField('value', value)}
                    sampleText={'switch_page_event'}
                    placeholder={translate('enterEventValue')}
                    required={true}
                />
            )
        )
    }

    const renderCreateEventButton = () => {
        return (
            <div className={styles.createButton}>
                <Button
                    mod={'fill'}
                    className={styles.button}
                    onClick={() => {
                        setEventModalData(defaultEventData)
                    }}
                >
                    {translate('create')}
                </Button>
            </div>
        )
    }

    const onChangeField = (field: string, value: string | number) => {
        const data: any = {
            ...eventModalData,
            [field]: value,
        }

        if (field === 'eventType') {
            data.key = null
            data.value = null
        }
        if (field === 'key') {
            data.value = null
        }

        setEventModalData(data)
    }

    const renderExternalStorageField = () => {
        return (
            <div className={styles.jsonBlockWrapper}>
                <div className={styles.label}>{translate('eventJsonModel')}</div>
                {eventModalData && eventModalData.json ? (
                    <div className={styles.jsonContainer}>
                        <ReactJson
                            src={eventModalData.json}
                            onSelect={(select) => {
                                const namespace = select.namespace.join('/')

                                setEventModalData({
                                    ...eventModalData,
                                    pathToField: namespace + '/' + select.name,
                                    value: select.value,
                                })

                                const objectKey = select.name
                                const objectKeys = document.querySelectorAll('.object-key')

                                objectKeys.forEach((key) => {
                                    const span = key.querySelector('span')

                                    if (span) {
                                        if (span.innerText === objectKey) {
                                            span.style.background = colors.blueAccent
                                        } else {
                                            span.style.background = 'none'
                                        }
                                    }
                                })
                            }}
                            displayDataTypes={false}
                            enableClipboard={false}
                            quotesOnKeys={false}
                        />
                    </div>
                ) : (
                    <div className={styles.selectJson}>
                        {translate('dragJsonDesc')}
                        <input className={styles.fileInput} type={'file'} onChange={handleFileUpload} />
                    </div>
                )}
            </div>
        )
    }

    const readJsonFile = async (file: Blob): Promise<any> => {
        try {
            return JSON.parse(await file.text())
        } catch (error) {
            return error
        }
    }
    const handleFileUpload = async (event: any) => {
        if (event.target.files) {
            const jsonData = await readJsonFile(event.target.files[0])

            setEventModalData({
                ...eventModalData,
                json: jsonData,
            })
        }
    }

    const getPriorityValue = (value: string) => {
        switch (value) {
            case 'low':
                return 0
            case 'medium':
                return 1
            case 'high':
                return 2
            default:
                return 0
        }
    }

    const getPriorityValueToString = (value: number) => {
        switch (value) {
            case 0:
                return 'low'
            case 1:
                return 'medium'
            case 2:
                return 'high'
            default:
                return value
        }
    }

    const getValue = (field: string) => {
        // @ts-ignore
        return eventModalData && eventModalData[field] ? eventModalData[field] : ''
    }

    const isValid = () => {
        return (
            eventModalData &&
            eventModalData.name &&
            eventModalData.eventType &&
            eventModalData.key &&
            eventModalData.value
        )
    }

    const updateEvent = () => {
        if (!eventModalData) return

        const data: IEvent = {
            name: eventModalData.name,
            eventType: eventModalData.eventType,
            key: eventModalData.key,
            priority: getPriorityValue(eventModalData.priority),
        }

        if (eventModalData.key !== 'external_source') {
            data.condition = {
                value: eventModalData.value,
                comparisonType: 'EQUAL',
            }
        }
        if (eventModalData.key === 'external_source') {
            data.conditionList = [
                {
                    value: eventModalData.value,
                    comparisonType: 'EQUAL',
                    pathToField: eventModalData.pathToField,
                },
            ]
        }
        if (eventModalData.id) {
            data.id = eventModalData.id
        }

        api.send(eventModalData.id ? 'updateBroadcastEvent' : 'createBroadcastEvent', data).then(() =>
            setEventModalData(null)
        )
    }

    const deleteEvent = () => {
        api.send('deleteBroadcastEvent', { eventIds: deleteConfirm }).then(() => {
            setDeleteConfirm([])
        })
    }

    const copyToClipboard = (data: any) => {
        const clipboardResultToastOptions = {
            autoClose: 3000,
            position: 'bottom-left',
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(data).then(
                () => {
                    Toast.info(translate('copiedToClipboard'), clipboardResultToastOptions)
                },
                () => {
                    //this.fallbackCopyToClipboard(data, clipboardResultToastOptions)
                }
            )
        } else {
            //this.fallbackCopyToClipboard(data, clipboardResultToastOptions)
        }
    }

    const renderTopContent = () => {
        return (
            <div>
                <SidebarMultiselectMenu>
                    <div className={cn(grid.p_md, styles.buttons)}>
                        <div className={styles.buttonWrapper}>
                            <MenuItem
                                data={{ name: 'delete' }}
                                onClick={() => {
                                    const filteredItems = selectedItems.filter((item: IEvent) => item.id !== undefined)

                                    setDeleteConfirm(filteredItems.map((item: IEvent) => item!.id))
                                }}
                            />
                        </div>
                    </div>
                </SidebarMultiselectMenu>
            </div>
        )
    }

    const renderSelectedInfoContent = () => {
        return (
            <div>
                <div className={styles.eventTitle}>
                    {translate('event')}
                    <br />
                    <b>{selectedInfo && selectedInfo.name}</b>
                </div>
                <div className={cn(grid.p_md, styles.buttons)}>
                    <div className={styles.buttonWrapper}>
                        <Button
                            onClick={() => {
                                setEventInfo(selectedInfo)
                            }}
                            mod={'clear'}
                        >
                            {translate('information')}
                        </Button>
                    </div>
                    {selectedInfo && selectedInfo.sample && (
                        <div className={styles.buttonWrapper}>
                            <Button
                                onClick={() => {
                                    setSampleInfo(selectedInfo.sample)
                                }}
                                mod={'clear'}
                            >
                                {translate('requestSample')}
                            </Button>
                        </div>
                    )}
                    <div className={styles.buttonWrapper}>
                        <Button
                            onClick={() => {
                                if (!selectedInfo) return

                                setEventModalData({
                                    id: selectedInfo.id,
                                    name: selectedInfo.name,
                                    eventType: selectedInfo.eventType,
                                    key: selectedInfo.key,
                                    value:
                                        selectedInfo.condition && selectedInfo.condition.value
                                            ? selectedInfo.condition.value
                                            : selectedInfo.conditionList && selectedInfo.conditionList.length
                                            ? selectedInfo.conditionList[0].value
                                            : null,
                                    priority: selectedInfo.priority,
                                    pathToField:
                                        selectedInfo.conditionList && selectedInfo.conditionList.length
                                            ? selectedInfo.conditionList[0].pathToField
                                            : null,
                                })
                            }}
                            mod={'clear'}
                        >
                            {translate('edit')}
                        </Button>
                    </div>
                    <div className={styles.buttonWrapper}>
                        <Button
                            onClick={() => {
                                const id = selectedInfo && selectedInfo.id

                                if (id) {
                                    setDeleteConfirm([id])
                                }
                            }}
                            mod={'clear'}
                        >
                            {translate('delete')}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    const renderSidebar = () => {
        return activeTab === 'broadcastEvents' ? (
            <Sidebar
                selectedSide="right"
                onChange={(list: any) => {
                    setSelectedItems(list)
                    setSelectedInfo(null)
                }}
                content={{
                    right: selectedItems.length
                        ? {
                              title: `${translate('countOfSelectedEvents')}: ${selectedItems.length}`,
                              options: selectedItems,
                          }
                        : null,
                }}
                isEmpty={{
                    right: !selectedItems.length && !selectedInfo,
                }}
                emptyText={{
                    right: {
                        title: translate('eventsNotSelected'),
                        description: translate('selectEventForAction'),
                    },
                }}
                topContent={{
                    right: selectedItems.length ? renderTopContent() : null,
                }}
                customContent={{
                    right: !selectedItems.length ? renderSelectedInfoContent() : null,
                }}
                onSelectInfo={() => {}}
            />
        ) : (
            <Sidebar
                selectedSide="right"
                onChange={(list: any) => {}}
                content={{
                    right: null,
                }}
                isEmpty={{
                    right: true,
                }}
                emptyText={{
                    right: {
                        title: translate('broadcastEventsHistory'),
                        description: (
                            <div>
                                {translate('logStored_1')}
                                <b>{(company && company.broadcastEventsStorageDays) || 30}</b>
                                {translate('logStored_2')}
                                <div
                                    className={styles.settingsLink}
                                    onClick={() => {
                                        changeLocation(`/${routes.settings.path}?open=broadcastEvents`)
                                    }}
                                >
                                    {translate('logStored_3')}
                                </div>
                            </div>
                        ),
                    },
                }}
            />
        )
    }

    return (
        <>
            <Content title={translate('broadcastEvents')} toolbar={renderSidebar()}>
                <div className={styles.tabs}>
                    <div
                        className={cn(styles.tab, activeTab === 'broadcastEvents' ? styles.activeTab : '')}
                        onClick={() => setActiveTab('broadcastEvents')}
                    >
                        {translate('broadcastEvents')}
                    </div>
                    <div
                        className={cn(styles.tab, activeTab === 'broadcastEventsHistory' ? styles.activeTab : '')}
                        onClick={() => setActiveTab('broadcastEventsHistory')}
                    >
                        {translate('broadcastEventsHistory')}
                    </div>
                </div>
                {activeTab === 'broadcastEvents' && (
                    <Catalog
                        type={'broadcastEvents'}
                        selectedItems={selectedItems}
                        onSelect={(items: IEvent[]) => {
                            setSelectedItems(items)
                        }}
                        onSelectInfo={(item: IEvent) => setSelectedInfo(item)}
                        selectedInfo={selectedInfo}
                        hideView={true}
                        disposition={'list'}
                        treeView={false}
                        customComponent={renderCreateEventButton}
                        hideSelection={true}
                        selectAllCheckbox={true}
                    />
                )}
                {activeTab === 'broadcastEventsHistory' && (
                    <Catalog
                        type={'broadcastEventsHistory'}
                        selectedItems={[]}
                        onSelectInfo={(item: IEvent) => {}}
                        selectedInfo={null}
                        hideView={true}
                        disposition={'list'}
                        treeView={false}
                        hideSelection={true}
                        selectAllCheckbox={true}
                    />
                )}
            </Content>
            {eventModalData && (
                <div className={styles.eventModal}>
                    <div className={styles.eventModalInner}>
                        <div className={styles.eventModalTitle}>
                            {translate(eventModalData.id ? 'updateEventTitle' : 'createEventTitle')}
                        </div>
                        <div className={styles.fieldWrapper}>
                            <InputField
                                label={translate('eventTitle')}
                                value={getValue('name')}
                                onChange={(value: string) => onChangeField('name', value)}
                                sampleText={translate('importantAlert')}
                                placeholder={translate('enterEventName')}
                                required={true}
                            />
                        </div>
                        <div className={styles.fieldWrapper}>
                            <Dropdown
                                value={getPriorityValueToString(eventModalData.priority)}
                                options={priorityOptions}
                                label={translate('priority')}
                                onChange={(value) => {
                                    onChangeField('priority', value.id)
                                }}
                            />
                        </div>
                        {eventModalData.priority !== undefined ? (
                            <div className={styles.fieldWrapper}>
                                <Dropdown
                                    value={getValue('eventType')}
                                    options={eventsOptions}
                                    label={translate('eventType')}
                                    onChange={(value) => {
                                        onChangeField('eventType', value.id)
                                    }}
                                />
                            </div>
                        ) : null}
                        {eventModalData.eventType && (
                            <div className={styles.fieldWrapper}>
                                <Dropdown
                                    value={eventModalData.key}
                                    options={getKeyOptions()}
                                    label={translate('key')}
                                    onChange={(value) => {
                                        onChangeField('key', value.id)
                                    }}
                                />
                            </div>
                        )}
                        {eventModalData.key === 'external_source' && (
                            <div className={styles.fieldWrapper}>{renderExternalStorageField()}</div>
                        )}
                        {eventModalData.pathToField && (
                            <div className={styles.fieldWrapper}>
                                <InputField
                                    label={translate('pathToField')}
                                    value={getValue('pathToField')}
                                    onChange={(value: string) => {}}
                                    disabled={true}
                                />
                            </div>
                        )}
                        <div className={styles.fieldWrapper}>{renderValueField(eventModalData)}</div>
                        <div className={styles.controls}>
                            <Button
                                mod={'withBorder'}
                                className={styles.button}
                                onClick={() => setEventModalData(null)}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button mod={'fill'} className={styles.button} onClick={updateEvent} disabled={!isValid()}>
                                {translate('create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {!!deleteConfirm.length && (
                <div className={styles.eventModal}>
                    <div className={styles.eventModalInner}>
                        <div className={styles.deleteInfoWrapper}>
                            <div className={styles.eventModalTitle}>{translate('eventDelete')}</div>
                            <div className={styles.eventModalText}>{translate('eventDeleteWarning')}</div>
                        </div>
                        <div className={styles.controls}>
                            <Button mod={'withBorder'} className={styles.button} onClick={() => setDeleteConfirm([])}>
                                {translate('cancel')}
                            </Button>
                            <Button mod={'fill'} className={styles.button} onClick={deleteEvent}>
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {eventInfo && (
                <div className={styles.eventModal}>
                    <div className={styles.eventModalInner}>
                        <div className={styles.eventModalTitle}>{translate('information')}</div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('eventTitle')}</div>
                            <div className={styles.eventValue}>{eventInfo.name}</div>
                        </div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('eventType')}</div>
                            <div className={styles.eventValue}>{translate(eventInfo.eventType || '')}</div>
                        </div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('key')}</div>
                            <div className={styles.eventValue}>
                                {translate(`${eventInfo.eventType}_${eventInfo.key}`)}
                            </div>
                        </div>
                        {eventInfo.conditionList && eventInfo.conditionList.length && (
                            <div className={styles.infoFieldWrapper}>
                                <div className={styles.eventLabel}>{translate('pathToField')}</div>
                                <div className={styles.eventValue}>{eventInfo.conditionList[0].pathToField}</div>
                            </div>
                        )}
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('value')}</div>
                            <div className={styles.eventValue}>
                                {eventInfo.condition &&
                                    eventInfo.condition.value &&
                                    translate(eventInfo.condition.value)}
                                {eventInfo.conditionList &&
                                    eventInfo.conditionList.length &&
                                    eventInfo.conditionList[0].value &&
                                    eventInfo.conditionList[0].value}
                            </div>
                        </div>
                        <div className={styles.control}>
                            <Button
                                mod={'fill'}
                                className={styles.button}
                                onClick={() => {
                                    setEventInfo(null)
                                }}
                            >
                                {translate('close')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {sampleInfo && sampleInfo.body && (
                <div className={styles.eventModal}>
                    <div className={styles.eventModalInner}>
                        <div className={styles.eventModalTitle}>{translate('requestSample')}</div>
                        <div className={styles.copyToClipboard}>
                            <Icon
                                type={'copyToClipboard'}
                                size={22}
                                tooltip={{
                                    title: translate('copyToClipboard'),
                                }}
                                onClick={() => {
                                    let dataText = ''

                                    dataText += translate('server') + ': ' + window.spconfig.ip_address + '\r'
                                    dataText += translate('method') + ': ' + sampleInfo.methodType + '\r'
                                    dataText += translate('eventId') + ': ' + sampleInfo.body.eventID + '\r'
                                    // dataText += translate('value') + ': ' + sampleInfo.body.value + '\r'
                                    // dataText += translate('token') + ': ' + sampleInfo.body.token

                                    copyToClipboard(dataText)
                                }}
                            />
                        </div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('server')}</div>
                            <div className={styles.eventValue}>{window.spconfig.ip_address}</div>
                        </div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('method')}</div>
                            <div className={styles.eventValue}>{sampleInfo.methodType}</div>
                        </div>
                        <div className={styles.infoFieldWrapper}>
                            <div className={styles.eventLabel}>{translate('eventId')}</div>
                            <div className={styles.eventValue}>{translate(sampleInfo.body.eventID)}</div>
                        </div>
                        {/*<div className={styles.infoFieldWrapper}>*/}
                        {/*    <div className={styles.eventLabel}>{translate('value')}</div>*/}
                        {/*    <div className={styles.eventValue}>{translate(sampleInfo.body.value || '—')}</div>*/}
                        {/*</div>*/}
                        {/*<div className={styles.infoFieldWrapper}>*/}
                        {/*    <div className={styles.eventLabel}>{translate('token')}</div>*/}
                        {/*    <div className={styles.eventValue}>{translate(sampleInfo.body.token || '—')}</div>*/}
                        {/*</div>*/}
                        <div className={styles.control}>
                            <Button
                                mod={'fill'}
                                className={styles.button}
                                onClick={() => {
                                    setSampleInfo(null)
                                }}
                            >
                                {translate('close')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
